import type { ApprovalLinePreviewData, ApprovalLinePreviewRow } from '../../../../lib/reports';
import type { ApprovalLineItem } from '@safetyhub/report-composer';

/**
 * cascade preview base row → `ApprovalLineItem` 변환 — **base row를 편집 행으로 옮기는 정본**.
 *
 * 섹션(standalone 초기화·현장 기본 되돌리기) 안의 로컬 함수였는데, 위임 모달도 같은 변환이
 * 필요해져(F-49 — 담당자를 바꿀 때 **위임이 소유한 칸만** 다시 해소한다) 순수 모듈로 뺐다.
 * 두 곳이 각자 손으로 조립하면 한쪽에만 필드가 빠져도 조용히 갈라진다 — 연락처 한 칸이 빠지면
 * 편집 모달이 채널 종류를 0개로 세어 「연락처가 없어 통지가 나가지 않습니다」를 띄운다(2026-09-09).
 *
 * 컴포넌트·훅·API에 의존하지 않는 순수 함수라 단위 spec으로 직접 잰다.
 */
export function buildItemFromPreviewBaseRow(row: ApprovalLinePreviewRow): ApprovalLineItem {
  // memberId를 직접 사용 — 이름 매칭 fallback 금지(매칭 실패로 memberId 유실 방지).
  const memberId = row.memberId ?? undefined;
  // 메타에서 담당자를 지정하지 않은 칸 — 칸은 살리고 담당자만 빈 상태로 둔다.
  // memberId 부재로 판정하면 "체인은 있으나 resolve 실패한 칸"까지 미지정으로 오인하므로
  // 서버가 메타 원본에서 판정해 내려준 assigneeUnset을 그대로 쓴다.
  if (row.assigneeUnset === true) {
    return {
      approvalLevel: row.level,
      label: row.label,
      _locked: row.locked,
      _assigneeUnset: true,
    } as ApprovalLineItem & { _locked: boolean };
  }
  return {
    memberId,
    member: memberId
      ? {
          id: memberId,
          name: row.memberName ?? '',
          title: row.memberTitle ?? null,
          // 연락처는 **서버가 해소한 값**(발송 사다리와 같은 함수)을 그대로 쓴다 —
          // 서명 행 매핑과 같은 규약이다. 여기서 떨어뜨리면 편집 모달이 통지 채널
          // 종류를 0개로 보고 연락처가 멀쩡한 결재자에게도 "통지가 나가지 않습니다"를
          // 띄운다(2026-09-09 위임 모달에서 재발 — 서명 행 경로만 고쳤던 자리다).
          phone: row.phone ?? null,
          email: row.email ?? null,
          accountEmail: row.accountEmail ?? null,
        }
      : row.memberName
        ? { id: '', name: row.memberName, title: row.memberTitle ?? null }
        : undefined,
    approvalLevel: row.level,
    label: row.label,
    // S76-A: **지정 방식을 접지 않는다.** 종전에는 author가 아니면 memberId 유무로
    // member/author 둘 중 하나로 접었고, 그래서 역할·위임으로 정의된 칸이 편집 모달을
    // 한 번 거치는 것만으로 "특정 인물"로 굳었다(prod 실측: 역할 지정 0건).
    // 서버가 preview에 실제 sourceType을 실어 보내므로 그대로 보존한다.
    sourceType: row.sourceType ?? (memberId ? 'member' : 'author'),
    _assignmentChain: row.assignmentChain,
    _locked: row.locked,
  } as ApprovalLineItem & { _locked: boolean };
}

/**
 * cascade preview baseRows → ApprovalLineItem[] 변환 헬퍼.
 * standalone 초기화 useEffect와 handleResetToSiteDefault 양쪽에서 재사용한다.
 */
export function buildItemsFromPreviewBaseRows(
  preview: ApprovalLinePreviewData,
): ApprovalLineItem[] {
  return preview.baseRows.map(buildItemFromPreviewBaseRow);
}
